import React, { useState, useEffect } from 'react';
import api from '../api';
import { ChevronRight, Trash2 } from 'lucide-react';
import { Sheet, Fab, ConfirmBox, plural } from '../components/AdminUi';

const EMOJIS = ['📋', '🚒', '🏆', '🏛️', '📚', '📝', '🔧', '🛠️', '🏗️', '🤝', '🎉', '💧', '⭐', '🔴', '🟡', '🟢', '🔵', '🟣'];

const countText = (n) => (n ? `${n} ${plural(n, 'zadatak', 'zadatka', 'zadataka')}` : 'još nije korištena');

// Panel kategorije: nova (category = null) ili uređivanje postojeće.
function CategorySheet({ category, onClose, onSaved, onShowTasks }) {
  const [name, setName] = useState(category?.name || '');
  const [emoji, setEmoji] = useState(category?.emoji || '📋');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const n = Number(category?.task_count || 0);

  const save = async () => {
    if (!name.trim()) return setError('Upiši naziv kategorije.');
    setBusy(true);
    try {
      if (category) await api.put(`/categories/${category.id}`, { name: name.trim(), emoji });
      else await api.post('/categories', { name: name.trim(), emoji });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Spremanje nije uspjelo.');
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/categories/${category.id}`);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Brisanje nije uspjelo.');
      setBusy(false);
    }
  };

  return (
    <Sheet
      onClose={onClose}
      head={
        <div className="adm-sh">
          <span className="adm-tile emoji" style={{ width: 48, height: 48, fontSize: 26 }}>{emoji || '📋'}</span>
          <div className="adm-main">
            <h3>{category ? category.name : 'Nova kategorija'}</h3>
            {category && (
              <p>
                {countText(n)}
                {n > 0 && <> · <button type="button" className="adm-link" style={{ padding: 0, fontSize: 15 }} onClick={() => onShowTasks(category.id)}>prikaži na Zadacima</button></>}
              </p>
            )}
          </div>
        </div>
      }
      footer={
        <button type="button" className="adm-btn primary" onClick={save} disabled={busy}>
          {category ? 'Spremi' : 'Dodaj kategoriju'}
        </button>
      }
    >
      <div className="adm-fl">Naziv</div>
      <input id="cat-name" className="adm-input" value={name} onChange={e => { setName(e.target.value); setError(''); }} placeholder="npr. Vježba" maxLength={30} />
      <div className="adm-fl">Ikona</div>
      <div className="adm-emgrid">
        {EMOJIS.map(e => (
          <button key={e} type="button" className={emoji === e ? 'on' : ''} onClick={() => setEmoji(e)}>{e}</button>
        ))}
      </div>
      <input id="cat-emoji" className="adm-input" style={{ width: 160 }} value={EMOJIS.includes(emoji) ? '' : emoji}
        onChange={e => setEmoji(e.target.value || '📋')} placeholder="ili upiši emoji" maxLength={8} />
      {error && <p className="adm-err">{error}</p>}
      {category && (confirmDel ? (
        <ConfirmBox
          title="Obriši kategoriju?"
          text={n ? `Uklonit će se s ${n} ${plural(n, 'zadatka', 'zadatka', 'zadataka')}. Zadaci ostaju, samo bez ove kategorije.` : 'Kategorija se ne koristi ni na jednom zadatku.'}
          onCancel={() => setConfirmDel(false)} onConfirm={remove} busy={busy} />
      ) : (
        <div className="adm-card" style={{ marginTop: 4 }}>
          <button type="button" className="adm-act red" onClick={() => setConfirmDel(true)}><Trash2 size={18} /> Obriši kategoriju</button>
        </div>
      ))}
    </Sheet>
  );
}

function Categories({ onShowTasks }) {
  const [categories, setCategories] = useState([]);
  const [sheet, setSheet] = useState(null);   // { category } | { category: null } za novu

  const fetchCategories = async () => {
    const res = await api.get('/categories');
    setCategories(res.data);
  };
  useEffect(() => { fetchCategories(); }, []);

  // Najkorištenije prve, pa abecedno.
  const sorted = [...categories].sort((a, b) => Number(b.task_count || 0) - Number(a.task_count || 0) || a.name.localeCompare(b.name, 'hr'));

  return (
    <div className="adm-page">
      <div className="adm-head"><h2>Kategorije</h2><span className="n">{categories.length}</span></div>
      {sorted.length === 0 ? (
        <div className="adm-empty">Još nema kategorija. Dodaj prvu gumbom +.</div>
      ) : (
        <div className="adm-card">
          {sorted.map(c => {
            const n = Number(c.task_count || 0);
            return (
              <button key={c.id} type="button" className="adm-it" onClick={() => setSheet({ category: c })}>
                <span className="adm-tile emoji">{c.emoji}</span>
                <div className="adm-main">
                  <div className={`adm-t${n ? '' : ' soft'}`}>{c.name}</div>
                  <div className="adm-s">{countText(n)}</div>
                </div>
                <ChevronRight size={18} className="adm-chev" />
              </button>
            );
          })}
        </div>
      )}
      <Fab onClick={() => setSheet({ category: null })} title="Nova kategorija" />
      {sheet && (
        <CategorySheet
          key={sheet.category?.id || 'new'}
          category={sheet.category}
          onClose={() => setSheet(null)}
          onSaved={() => { setSheet(null); fetchCategories(); }}
          onShowTasks={(id) => { setSheet(null); onShowTasks && onShowTasks({ categories: [id] }); }}
        />
      )}
    </div>
  );
}

export default Categories;
